/**
 * en / fr / es strings for the bulk-upload modal frame, the file step, the example table headers
 * (`ExampleFileStructureTable/ExampleFileStructureTableConfig.tsx`) and the part search in
 * `BulkUploadHelpers.tsx`. `%name%` tokens are replaced in code (`addItemsToShoppingCart` in
 * `BulkUploadModal.tsx`, `noResultFound` in `BulkUploadHelpers.tsx`).
 *
 * Not read by any file: `dropFileHere`, `clickToSelect`, `tipWithDownload`, `optionnal`. The `fr`
 * value of `BulkUpload` is Spanish text.
 */
import { LanguageContent } from "@ryerson/frontend.common";
/** Shape of each language's strings in {@link BulkUploadModalContent}. */
export interface BulkUploadModalText {
	BulkUpload: string;
	processingFile: string;
	pleaseWait: string;
	cancel: string;
	continue: string;
	cancelBulkUpload: string;
	confirmCancel: string;
	selectFile: string;
	mapFields: string;
	reviewItems: string;
	toolHelpUpload: string;
	uploadFile: string;
	dropFileHere: string;
	clickToSelect: string;
	tipCsvFile: string;
	tipNoQuantities: string;
	tipConvenience: string;
	tipXLSTemplate: string;
	tipWithDownload: string;
	exampleXLS: string;
	optionnal: string;
	upload: string;
	noResultFound: string;
	quote: string;
	partNumber: string;
	quantity: string;
	quantityUom: string;
	priceUom: string;
	customerRefNumber: string;
	lineItemNotes: string;
	lineLevelPO: string;
	deliveryDate: string;
	invalidContent: string;
	addItemsToShoppingCart: string;
	shoppingCart: string;
	shoppingList: string;
	item: string;
}

/** The bulk-upload modal's strings by language. */
export const BulkUploadModalContent: LanguageContent<BulkUploadModalText> = {
	en: {
		quote: "Quote",
		BulkUpload: "Bulk Upload",
		processingFile: "We are processing the file...",
		pleaseWait: "Please wait.",
		cancel: "Cancel",
		continue: "Continue",
		cancelBulkUpload: "Cancel the Bulk Upload",
		confirmCancel:
			"You haven't finished the bulk upload and the items haven't been added. Would you like to continue or cancel?",
		selectFile: "Select File",
		mapFields: "Map the Fields",
		reviewItems: "Review Items",
		toolHelpUpload: "This tool will help you bulk upload multiple products.",
		uploadFile: "Upload file",
		dropFileHere: "Drop the file here or ",
		clickToSelect: "click to select",
		tipCsvFile: "If the file is a csv file, please use a comma as the separator",
		tipNoQuantities: "Any items without quantities listed will not be imported",
		tipConvenience: "For your convenience, we have provided an ",
		tipXLSTemplate: "XLS template ",
		tipWithDownload: "with the download",
		exampleXLS: "Example of XLS File Structure",
		optionnal: "(optional)",
		upload: "Upload",
		noResultFound: "No Results Found for %searchString%",
		partNumber: "Part Number",
		quantity: "Quantity",
		quantityUom: "Quantity Unit Of Measure",
		priceUom: "Price Unit Of Measure",
		customerRefNumber: "Customer Reference Number",
		lineItemNotes: "Line Item Notes",
		lineLevelPO: "Line Level PO Number",
		deliveryDate: "Future Delivery Date",
		invalidContent: "No data found in file",
		addItemsToShoppingCart: "Add %validatedItems% %Items% to the %ShoppingCart%",
		shoppingCart: "Shopping cart",
		shoppingList: "Shopping List",
		item: "Item",
	},
	fr: {
		quote: "Devis",
		BulkUpload: "Carga masiva",
		processingFile: "Nous traitons le fichier…",
		pleaseWait: "Veuillez patienter.",
		cancel: "Annuler",
		continue: "Continuer",
		cancelBulkUpload: "Annuler le chargement de vrac",
		confirmCancel:
			"Vous n’avez pas terminé le chargement de vrac, et les articles n’ont pas été ajoutés. Souhaitez-vous continuer ou annuler?",
		selectFile: "Sélectionner le fichier",
		mapFields: "Faire correspondre les champs",
		reviewItems: "Passer en revue les articles",
		toolHelpUpload:
			"Cet outil vous permet d’effectuer un chargement de vrac de plusieurs produits.",
		uploadFile: "Téléverser le fichier",
		dropFileHere: "Déposez le fichier ici ou ",
		clickToSelect: "cliquez pour le sélectionner",
		tipCsvFile:
			"Si le fichier est au format .csv, veuillez utiliser une virgule comme séparateur.",
		tipNoQuantities:
			"Les articles dont les quantités ne sont pas indiquées ne seront pas importés.",
		tipConvenience: "Pour votre convenance, nous fournissons un ",
		tipXLSTemplate: "patron de fichier XLS ",
		tipWithDownload: "avec le téléchargement",
		exampleXLS: "Exemple de structure de fichier .xls",
		optionnal: "(facultatif)",
		upload: "Téléverser",
		noResultFound: "Aucun résultat trouvé pour %searchString%",
		partNumber: "N° de pièce",
		quantity: "Quantité",
		quantityUom: "Unité de mesure Quantité",
		priceUom: "Unité de mesure Prix",
		customerRefNumber: "N° de référence client",
		lineItemNotes: "Remarques sur le type d’article",
		lineLevelPO: "BC Niveau de l’article",
		deliveryDate: "Future date de livraison",
		invalidContent: "Aucune donnée trouvée dans le fichier",
		addItemsToShoppingCart: "Ajouter %validatedItems% %Items% au %ShoppingCart%",
		shoppingCart: "Panier",
		shoppingList: "Liste d’achats",
		item: "Article",
	},
	es: {
		quote: "Cotizar",
		BulkUpload: "Subir a granel",
		processingFile: "Estamos procesando el archivo...",
		pleaseWait: "Espere.",
		cancel: "Cancelar",
		continue: "Continuar",
		cancelBulkUpload: "Cancelar la carga masiva",
		confirmCancel:
			"No ha finalizado la carga masiva y los artículos no se han agregado. ¿Desea continuar o cancelar?",
		selectFile: "Seleccionar archivo",
		mapFields: "Mapear los campos",
		reviewItems: "Revisar artículos",
		toolHelpUpload:
			"Esta herramienta lo ayudará a realizar una carga masiva de múltiples productos.",
		uploadFile: "Cargar archivo",
		dropFileHere: "Arrastre el archivo hasta aquí o ",
		clickToSelect: "haga clic para seleccionar",
		tipCsvFile: "Si se trata de un archivo csv, utilice una coma como separador",
		tipNoQuantities: "No se importarán los artículos sin las cantidades indicadas",
		tipConvenience: "Para su comodidad, le proporcionamos un",
		tipXLSTemplate: "plantilla de fichero XLS ",
		tipWithDownload: "con la descarga",
		exampleXLS: "Ejemplo de estructura de un archivo XLS",
		optionnal: "(opcional)",
		upload: "Cargar",
		noResultFound: "No se encontraron resultados para %searchString%",
		partNumber: "N° de pieza",
		quantity: "Cantidad",
		quantityUom: "Unidad de mensurad cantidad",
		priceUom: "Unidad de mensura precio",
		customerRefNumber: "N° de referencia cliente",
		lineItemNotes: "Notas de artículos de línea",
		lineLevelPO: "Pedido de nivel de línea",
		deliveryDate: "Futura fecha de entrega",
		invalidContent: "No se encontraron datos en el archivo",
		addItemsToShoppingCart: "Agregar %validatedItems% %Items% al %ShoppingCart%",
		shoppingCart: "Carrito de compras",
		shoppingList: "Lista de compra",
		item: "Artículo",
	},
};
